import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";

import type { ISelectedItem } from "@/data/model/Bill";
import type { IPaymentIntent, IPaymentMethod } from "@/data/model/Customer";
import type { IService } from "@/data/model/Service";
import { config } from "@/data/util/Config";

import { translations } from "@/shared/localization/translations";

import { PayContext } from "@/operator/movedComponents/PayContext";
import type { IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";

export const usePaymentStore = defineStore("payment", () => {
    const payContext = new PayContext(0);


    const lastPaymentMethodsData = ref<IPaymentMethodsReportData | null>(null);
    // state
    const paymentService = ref<IService | null>(null);
    const payLaterPaymentMethod = {
        id: config.payLaterPaymentMethod,
        type: config.payLaterPaymentMethod,
        provider: undefined,
        defaultProvider: null,
        name: config.payLaterPaymentMethod,
        localizedName: {
            de: "Später bezahlen",
            en: "Pay later",
            fr: "Payer plus tard",
            it: "Pagare più tardi",
        },
        imagePath: "/assets/img/brands/pay_later.svg",
        mode: 1,
        order: 9,
        defaultEnabled: true,
        isCardScheme: false,
        displayCollectiveCardIcon: true,
        isProviderEditable: false,
        useCollectiveCardIcon: false,
        isEditEnabled: false,
        isPaymentDevice: false,
        allowTipOnlyOnDevice: false,
        revenueEffective: true,
        isTipAllowed: true,
    } as IPaymentMethod;
    const isLoadingPaymentIntent = ref(false);
    const isProcessingPayment = ref(false);

    // actions
    async function setPaymentService(service: IService | null) {
        paymentService.value = service;
    }

    async function setLastPaymentMethodsData(service: IPaymentMethodsReportData | null) {
        lastPaymentMethodsData.value = service;
    }

    async function prepareForCheckout(): Promise<IPaymentIntent> {
        try {
            isLoadingPaymentIntent.value = true;
            if (!paymentService.value) throw translations.pay.noActivePaymentService;

            await payContext.prepareForCheckout(paymentService.value, undefined);
            if (!payContext.intent) throw translations.cart.notAbleToPrepareForCheckout;

            return payContext.intent;
        } finally {
            isLoadingPaymentIntent.value = false;
        }
    }

    async function pay(
        consumption: number,
        tip: number,
        selectedItems: ISelectedItem[],
        paymentMethod: IPaymentMethod
    ): Promise<void> {
        try {
            isProcessingPayment.value = true;
            if (!paymentService.value) throw translations.pay.noActivePaymentService;

            payContext.tipAmount = tip;
            payContext.selectedPaymentMethod = paymentMethod;
            await payContext.pay(paymentService.value, null, true, consumption, tip, selectedItems);
        } finally {
            isProcessingPayment.value = false;
        }
    }

    return {
        // state
        paymentService,
        payLaterPaymentMethod,
        isLoadingPaymentIntent,
        isProcessingPayment,
        lastPaymentMethodsData,
        
        // getters

        // actions
        setLastPaymentMethodsData,
        setPaymentService,
        prepareForCheckout,
        pay,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePaymentStore, import.meta.hot));
}
